import React, { useRef, useEffect, useState } from "react";

interface ScreenContainerProps {
  children: React.ReactNode;
  className?: string;
  targetWidth?: number;
  targetHeight?: number;
}

export const ScreenContainer: React.FC<ScreenContainerProps> = ({
  children,
  className = "",
  targetWidth = 1920,
  targetHeight = 1080,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (!container || !content) return;

    const updateScale = () => {
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Calculate scale to fit content within container (object-fit: contain)
      const scaleX = containerWidth / targetWidth;
      const scaleY = containerHeight / targetHeight;
      const newScale = Math.min(scaleX, scaleY);

      // Calculate horizontal centering
      const scaledWidth = targetWidth * newScale;
      const newTranslateX = (containerWidth - scaledWidth) / 2;

      const scaledHeight = targetHeight * newScale;
      const newTranslateY = (containerHeight - scaledHeight) / 2;

      setScale(newScale);
      setTranslate({ x: newTranslateX, y: newTranslateY });
    };

    // Initial scale calculation
    updateScale();

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    resizeObserver.observe(container);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [targetWidth, targetHeight]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full overflow-hidden ${className}`}
    >
      <div
        ref={contentRef}
        style={{
          width: targetWidth,
          height: targetHeight,
          transform: `translate(${translate.x}px, ${translate.y}px ) scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};
